'use client'

import { useId, useState, ReactNode } from 'react'
import { Plus, Minus } from 'lucide-react'

export type AccordionItemData = {
  title: string
  models?: string[]
  content?: ReactNode
}

export function AccordionList({ items, heading }: { items: AccordionItemData[]; heading?: string }) {
  const [openSet, setOpenSet] = useState<Set<string>>(new Set())
  const baseId = useId()

  const toggle = (key: string) => {
    setOpenSet((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  return (
    <div className="w-full">
      {heading && (
        <h3 className="text-2xl sm:text-3xl font-bold tracking-tight text-gray-900 mb-6 sm:mb-8 text-center">{heading}</h3>
      )}
      <div className="space-y-4">
        {items.map((item, index) => {
          const id = `${baseId}-${index}-${item.title.replace(/\s+/g, '-')}`
          const isOpen = openSet.has(item.title)
          return (
            <div
              key={item.title}
              role="button"
              tabIndex={0}
              aria-expanded={isOpen}
              aria-controls={`${id}-panel`}
              onClick={() => toggle(item.title)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  toggle(item.title)
                }
              }}
              className={`rounded-xl border bg-white shadow-sm focus:outline-none ${
                isOpen ? 'border-blue-300 ring-1 ring-blue-200' : 'border-gray-200'
              }`}
            >
              <div className="px-5 sm:px-6 py-4 sm:py-5 flex items-center justify-between">
                <h4 id={`${id}-button`} className="text-lg sm:text-xl font-semibold text-gray-900">
                  {item.title}
                </h4>
                {isOpen ? <Minus className="h-5 w-5 text-gray-700" /> : <Plus className="h-5 w-5 text-gray-700" />}
              </div>
              <div
                id={`${id}-panel`}
                role="region"
                aria-labelledby={`${id}-button`}
                className={`px-5 sm:px-6 pt-0 overflow-hidden transition-all duration-300 ease-out ${
                  isOpen ? 'max-h-96 opacity-100 pb-5 sm:pb-6' : 'max-h-0 opacity-0'
                }`}
              >
                {item.content ? (
                  <div className="text-gray-900">{item.content}</div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-gray-900">
                    {(item.models || []).map((model) => (
                      <div key={model} className="flex items-center text-sm sm:text-base">
                        <span className="w-2 h-2 bg-blue-600/90 rounded-full mr-3"></span>
                        <span className="break-words">{model}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}


